// Emailing someone ABOUT a claim — one place, because 1.0 had one place
// (BusinessLayer/Utils/EmailHelper.cs) and every screen that mailed an assessor
// went through it.
//
// Two rules live here:
//
// 1. THE SUBJECT (EmailHelper.GetSubjectForAssessor). Karen 2026-07-22: "LA ref
//    & Claim # in the subject — same as 1.0".
//      <Assessor/LA Ref#> - GPS<log#> - <Insurance Claim#> - <Client> - <Address>
//    " - " separated, empty parts skipped, "GPS" glued to the number, empty
//    body. Twenty years of muscle memory expects the facts in the subject.
//
// 2. THE CC (Person.CC, on the contact). 1.0 copies whoever that contact's CC
//    field names — the assessor's own supply-chain inbox, or, on the five
//    dedicated "... INVOICES" contacts, our accounts@ and support@. That is
//    what "click Invoices To and it emails them, cc Accounts and Support"
//    (Karen Renall 2026-08-05, #11) actually is: data, not a hardcoded pair.
//
// THE SEPARATOR IS A SEMICOLON. 1.0 stores multi-address fields ;-separated
// and hands them to mailto: as-is, and that is what the office's Outlook
// wants. RFC 6068 says comma, and this file used to normalise to it — which
// broke the very rule above: Outlook (with its default "commas separate
// addresses" option OFF) took "accounts@gps1.co.nz,support@gps1.co.nz" as ONE
// address and refused to send (KS 2026-09-15, job 76558: "there is a comma
// between accounts and support so it brings up error when sending"). The
// data in System Data was right; the join here was the fault. 1.0's
// EmailHelper carries a commented-out attempt at the comma ("Lets see if this
// works") — it had evidently been tried there too.

// ";" or "," separated blob -> ;-separated, blanks dropped.
export const ADDRESS_SEPARATOR = ";";
export function addressList(raw) {
  if (!raw) return "";
  return String(raw)
    .split(/[;,]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .join(ADDRESS_SEPARATOR);
}

export function claimEmailSubject(claim) {
  if (!claim) return "";
  return [
    claim.assessor_ref,
    claim.claim_number ? `GPS${claim.claim_number}` : "",
    claim.insurance_claim_number,
    claim.client_name,
    claim.street_address,
  ]
    .filter(Boolean)
    .join(" - ");
}

// THE CLIENT gets a different subject, and always did. The 1.0 header also
// uses this address-based subject when emailing the assigned PM. It builds it in two
// places that agree exactly (Details.aspx.cs:507 GetEmailAddressDisplay and
// Controls/ClientInfo.ascx.cs:178):
//
//     GPS <log#> - INSURANCE WORKS AT <street>, <suburb>
//
// So: "GPS" and the number separated by a SPACE (the assessor line glues them),
// and none of the assessor ref, insurance claim number or client name — which is
// the point. Those are our references and the insurer's; a homeowner reading
// their inbox wants to know it is about the works at their address.
//
// Marissa saw ours sending the assessor subject to clients on 2026-08-11 —
// "WI#183109 - GPS76468 - P045185100 - Fatina Aweidah - 3 Askew Place…" — and
// Karen Sweatman confirmed the 1.0 wording, "only for client emails".
//
// ONE KNOWN DIFFERENCE, and it is a data-model one rather than a choice: 1.0
// holds a structured PhysicalAddress and prints Street + Suburb, stopping short
// of the city. 2.0 keeps one `street_address` field, so the city rides along if
// the address carries it ("…, Farm Cove, Pakuranga" where 1.0 says "…, Farm
// Cove"). Splitting on commas to trim it would be guessing at how somebody typed
// their address, which is a worse failure than one extra place name.
export function clientEmailSubject(claim) {
  if (!claim) return "";
  const parts = [];
  if (claim.claim_number) parts.push(`GPS ${claim.claim_number}`);
  if (claim.street_address) parts.push(`INSURANCE WORKS AT ${claim.street_address}`);
  return parts.join(" - ");
}

// The href for "email this contact about this claim". Returns "" when there is
// no address to send to, so callers render plain text rather than a dead link.
export function claimMailtoHref(to, claim, cc = "", { client = false } = {}) {
  const recipients = addressList(to);
  if (!recipients) return "";
  const params = [];
  const ccList = addressList(cc);
  if (ccList) params.push(`cc=${encodeURIComponent(ccList)}`);
  // `client: true` for the homeowner or the header's PCO/PM link — both use
  // clientEmailSubject in 1.0's ClientInfo control. The assessor and four routing
  // contacts get the reference-laden line.
  const subject = client ? clientEmailSubject(claim) : claimEmailSubject(claim);
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  return `mailto:${recipients}${params.length ? `?${params.join("&")}` : ""}`;
}

// Hover text that says who is actually going to be on the email.
export function claimMailtoTitle(to, cc = "") {
  const ccList = addressList(cc);
  return `Email ${addressList(to)}${ccList ? `, cc ${ccList}` : ""} — claim details prefilled in the subject`;
}
